import { arrayRemove, arrayUnion, collection, doc, setDoc, updateDoc, writeBatch } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import Grouping, { GROUPING_SCOPES, GROUPING_STATUSES, GROUPING_TYPES } from '../types/Grouping';
import League from '../types/League';

export const useLeagueDivisionMutator = () => {
  const { isLeagueAdmin, isOrgAdmin, userState } = useData();
  const { isGod } = useAuth();

  const addDivision = async (league: League, grouping: Grouping): Promise<Grouping | null> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      return null;
    }

    if (!isLeagueAdmin(league.itemId, league.orgId) && !isOrgAdmin(league.orgId) && !isGod) {
      console.error('Permission denied: addDivision');
      return null;
    }

    try {
      const divisionRef = grouping.itemId
        ? doc(firestore, `org/${league.orgId}/division`, grouping.itemId)
        : doc(collection(firestore, `org/${league.orgId}/division`));

      const newDivision: Grouping = {
        ...grouping,
        itemId: divisionRef.id,
        orgId: league.orgId,
        orgName: league.orgName,
        leagueId: league.itemId,
        scope: GROUPING_SCOPES.LEAGUE,
        type: GROUPING_TYPES.DIVISION,
        members: grouping.members ?? [],
      };

      await setDoc(divisionRef, newDivision);
      return newDivision;
    } catch (error) {
      console.error('Error adding division:', error);
      return null;
    }
  };

  const updateDivision = async (orgId: string, division: Partial<Grouping>): Promise<void> => {
    if (!division.itemId || !division.leagueId) {
      console.error('Missing required fields for updateDivision');
      return;
    }

    if (!isLeagueAdmin(division.leagueId, orgId) && !isOrgAdmin(orgId) && !isGod) {
      console.error('Permission denied: updateDivision');
      return;
    }

    try {
      const protectedFields = ['itemId', 'orgId', 'orgName', 'scope', 'leagueId', 'type'];
      const { itemId, ...updates } = division;
      const safeUpdates = isGod
        ? updates
        : Object.fromEntries(Object.entries(updates).filter(([key]) => !protectedFields.includes(key)));

      const divisionRef = doc(firestore, `org/${orgId}/division/${itemId}`);
      await updateDoc(divisionRef, safeUpdates);
    } catch (error) {
      console.error('Error updating division:', error);
    }
  };

  const deleteDivision = async (orgId: string, division: Grouping): Promise<void> => {
    if (!division.itemId || !division.leagueId) {
      console.error('Missing required fields for deleteDivision');
      return;
    }

    if (!isLeagueAdmin(division.leagueId, orgId) && !isOrgAdmin(orgId) && !isGod) {
      console.error('Permission denied: deleteDivision');
      return;
    }

    try {
      const divisionRef = doc(firestore, `org/${orgId}/division/${division.itemId}`);
      await updateDoc(divisionRef, { status: GROUPING_STATUSES.DELETED });
    } catch (error) {
      console.error('Error deleting division:', error);
    }
  };

  const changePlayerDivision = async (
    orgId: string,
    leagueId: string,
    playerId: string,
    fromDivisionId: string | null,
    toDivisionId: string | null
  ): Promise<void> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User not ready');
      return;
    }

    if (!isLeagueAdmin(leagueId, orgId) && !isOrgAdmin(orgId) && !isGod) {
      console.error('Permission denied: changePlayerDivision');
      return;
    }

    try {
      const batch = writeBatch(firestore);

      if (fromDivisionId) {
        const fromRef = doc(firestore, `org/${orgId}/division/${fromDivisionId}`);
        batch.update(fromRef, { members: arrayRemove(playerId) });
      }

      if (toDivisionId) {
        const toRef = doc(firestore, `org/${orgId}/division/${toDivisionId}`);
        batch.update(toRef, { members: arrayUnion(playerId) });
      }

      await batch.commit();
    } catch (error) {
      console.error('Error changing player division:', error);
    }
  };

  return {
    addDivision,
    updateDivision,
    deleteDivision,
    changePlayerDivision,
  };
};
